import {
  takeLatest, select, call,
} from 'redux-saga/effects';
import { message } from 'antd';
import {
  exportData as exportRequest,
  exportWidgetData as exportWidgetRequest,
  exportAlertEvents as exportAlertEventsRequest,
  exportViewerModule as exportViewerModuleRequest,
} from '@api/export';
import { autoRequest } from '@api/createRequestStore';
import {
  EXPORT_COMPARE_DASHBOARD,
  EXPORT_ERROR_DIAGNOSTICS,
  EXPORT_WIDGET_COMPARE,
  EXPORT_WIDGET_ERROR_BIRD_CHART,
  EXPORT_WIDGET_ERROR_BAR_CHART,
  EXPORT_WIDGET_ERROR_TOP_TRENDS_ERROR_TABLE,
  EXPORT_WIDGET_ERROR_SESSIONS_ERROR_TABLE,
  EXPORT_REALTIME,
  EXPORT_TIMELINE,
  EXPORT_ML,
} from '@utils/constants';
import moment from 'moment';
import { saveAs } from '@utils';
import * as types from './types';
import {
  compareDashboardExportMetaSelector,
  errorDashboardExportMetaSelector,
  compareWidgetMetaSelector,
  errorBirdChartMetaSelector,
  errorBarChartMetaSelector,
  errorTopTrendsMetaSelector,
  errorSessionsMetaSelector,
  viewerTimelineMetaSelector,
} from './selectors';

import {
  nocMetaSelector,
} from './nocSelectors';

const TS_FORMAT = 'MMM-DD-YYYY_HH:mm:ss';

function* getCompareDashboardMeta() {
  return yield select(compareDashboardExportMetaSelector);
}

function* getErrorDashboardMeta() {
  return yield select(errorDashboardExportMetaSelector);
}

function* getCompareWidgetMeta() {
  return yield select(compareWidgetMetaSelector);
}

function* getErrorBirdChartMeta() {
  return yield select(errorBirdChartMetaSelector);
}

function* getErrorBarChartMeta() {
  return yield select(errorBarChartMetaSelector);
}

function* getErrorTopTrendsMeta() {
  return yield select(errorTopTrendsMetaSelector);
}

function* getErrorSessionsMeta() {
  return yield select(errorSessionsMetaSelector);
}

function* getNOCMeta() {
  return yield select(nocMetaSelector);
}

function* getTimelineMeta() {
  return yield select(viewerTimelineMetaSelector);
}

const exportHandler = {
  [EXPORT_COMPARE_DASHBOARD]: getCompareDashboardMeta,
  [EXPORT_ERROR_DIAGNOSTICS]: getErrorDashboardMeta,
  [EXPORT_WIDGET_COMPARE]: getCompareWidgetMeta,
  [EXPORT_WIDGET_ERROR_BIRD_CHART]: getErrorBirdChartMeta,
  [EXPORT_WIDGET_ERROR_BAR_CHART]: getErrorBarChartMeta,
  [EXPORT_WIDGET_ERROR_TOP_TRENDS_ERROR_TABLE]: getErrorTopTrendsMeta,
  [EXPORT_WIDGET_ERROR_SESSIONS_ERROR_TABLE]: getErrorSessionsMeta,
  [EXPORT_REALTIME]: getNOCMeta,
  [EXPORT_TIMELINE]: getTimelineMeta,
};

function* exportData({ context }) {
  const meta = yield exportHandler[context]();
  try {
    const resp = yield call(exportRequest, meta);
    const filename = `${context}_${moment().format(TS_FORMAT)}.csv`;
    saveAs(resp.data, filename);
  } catch (error) {
    yield call(message.error, error.message || 'Unknown error.');
  }
}

function* exportAlertEvents({ alertIds }) {
  try {
    const resp = yield call(exportAlertEventsRequest, { ids: alertIds });
    const filename = 'Event List.csv';
    saveAs(resp.data, filename);
  } catch (error) {
    yield call(message.error, error.message || 'Unknown error.');
  }
}

function* exportWidgetData({ context, metric }) {
  let newContext = context;

  if (newContext === EXPORT_WIDGET_ERROR_TOP_TRENDS_ERROR_TABLE) {
    const errorStore = yield select(errorDashboardExportMetaSelector);
    newContext = errorStore.viewerSessions.visible
      ? EXPORT_WIDGET_ERROR_SESSIONS_ERROR_TABLE
      : EXPORT_WIDGET_ERROR_TOP_TRENDS_ERROR_TABLE;
  }
  const meta = yield exportHandler[newContext](metric);
  try {
    const resp = yield autoRequest(exportWidgetRequest, types.EXPORT_WIDGET_DATA, meta);
    const filename = `${newContext}_${moment().format(TS_FORMAT)}.csv`;
    saveAs(resp, filename);
  } catch (error) {
    yield call(message.error, error.message || 'Unknown error.');
  }
}

function* exportViewerModuleData({ config }) {
  try {
    const resp = yield call(exportViewerModuleRequest, { ...config });
    const filename = `Viewer Module_${moment().format(TS_FORMAT)}.csv`;
    saveAs(resp.data, filename);
  } catch (error) {
    yield call(message.error, error.message || 'Unknown error.');
  }
}

function* watchExportData() { // for dashboards
  yield takeLatest([
    types.EXPORT_DATA,
  ], exportData);
}

function* watchExportWidgetData() { // for widget
  yield takeLatest([
    types.EXPORT_WIDGET_DATA,
  ], exportWidgetData);
}

function* watchExportAlertEvents() { // for dashboards
  yield takeLatest([
    types.EXPORT_ALERT_EVENTS,
  ], exportAlertEvents);
}

function* watchExportVMData() { // for Viewer Module
  yield takeLatest([
    types.EXPORT_VM_DATA,
  ], exportViewerModuleData);
}

export default [
  watchExportData,
  watchExportWidgetData,
  watchExportAlertEvents,
  watchExportVMData,
];
